import React, { useEffect, useState, createRef } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { withRouter } from 'react-router-dom';
import { withStyles } from '@material-ui/core/styles';
import { FileDropZone, excel } from 'components/layout/mui-dropzone';
import {
  setImportRows, setConfirmationModalState, submitImport, setImportRawData,
  setImportRowsSkippedCount, setImportLoadingOverlayState, revalidateImportRows, appNotify
} from 'store/actions';
import { importTypeStateSelector, canImportSelector } from 'store/selectors';
import {
  FormControlLabel,
  Button,
  Typography,
  Tooltip,
  Divider,
  Switch,
  Paper
} from '@material-ui/core';
import ImportTitlesGrid from 'components/underwrite/acquisition/import/importTitles/ImportTitlesGrid';
import { ImportExpander, InlineBadge } from 'components/underwrite/shared/import';
import xlsxSampleSheet from 'static/assets/ReVUe_SampleImportTitles.xlsx';
import { sendClientLog } from 'services/logService';
import { Constants } from 'utilities/constants';
import { format } from 'date-fns';
import dataGridExtension from 'components/devExtreme/dataGridExtension';

const gridRef = createRef();

const ImportTitlesControls = ({ classes, history, canImportTitles, setConfirmationModalState, submitImport,
  setImportRawData, setImportRows, setImportRowsSkippedCount, revalidateImportRows, appNotify,
  redirectToImportStatusTab, importRowsTitles, titlesGridEditDirty, importRowsSkippedTitles, setImportLoadingOverlayState
}) => {
  const [filterByErrors, setFilterByErrors] = useState(false);
  const hasErrors = importRowsTitles.some(row => row.invalidColumns?.length > 0);
  const maxCountExceeded = importRowsTitles?.length > Constants.ImportMaxCount.TITLES;

  useEffect(() => {
    sendClientLog('Info', 'Import - User loaded Titles import tab');
  }, []);

  useEffect(() => {
    const dataSource = gridRef.current.instance.getDataSource();
    if (!(dataSource && importRowsTitles)) return;

    if (filterByErrors) {
      if (!hasErrors) { // If no more errors (or if no importRows), automatically set filter toggle off and remove from grid
        setFilterByErrors(false);
        gridRef.current.instance.clearFilter();
      } else { // Else, apply filter to grid data
        gridRef.current.instance.filter(row => {
          return row.invalidColumns?.length > 0;
        });
      }
    } else if (gridRef.current.instance.filter()) { // If filter toggle is off, clear filter from grid
      gridRef.current.instance.clearFilter();
    }
  }, [importRowsTitles, filterByErrors])

  const onDropZoneFilesAdded = async (addedFiles) => {
    let combinedData = [];
    try {
      setImportLoadingOverlayState({ // Start displaying loading here since sheets with high row count can take some time. Loading will be turned off in importRawDataSaga after validation is finished.
        isLoading: true,
        message: `Loading file...`
      });
      for (const file of addedFiles) {
        if (excel.excelMimeTypes.indexOf(file.type.toLowerCase()) >= 0) {
          const spreadsheet = await excel.parseExcel(file);
          // Only importing first sheet, should import all?
          const DragAndDropData = spreadsheet[Object.keys(spreadsheet)[0]];
          combinedData = combinedData.concat(DragAndDropData);
          // setImportRawData(spreadsheet);
        } else {
          // const text = await files.loadFileAsText(file);
          //console.log(text);
        }
      }
      if (filterByErrors) {
        setFilterByErrors(false);
      }
      setImportRawData(combinedData, 'file');
    } catch {
      setImportLoadingOverlayState({
        isLoading: false,
        message: null
      });
    }
  }
  const onDropZoneFilesRejected = files => {
    //console.log('Files rejected:', files);
  }

  const handlePaste = async (e) => {
    // First, ask the Permissions API if we have some kind of access to
    // the "clipboard-read" feature.
    const result = await navigator.permissions.query({ name: "clipboard-read" });
    // If permission to read the clipboard is granted or if the user will
    // be prompted to allow it, we proceed.
    if (result.state === 'granted' || result.state === 'prompt') {
      try {
        const data = await navigator.clipboard.readText();
        // console.log(data);
        if (filterByErrors) {
          setFilterByErrors(false);
        }
        setImportRawData(data, 'paste');
      } catch (err) {
        // alert('Paste Error: ' + JSON.stringify(err));
        // Snackbar instead of alert message, unsure how to reproduce this error. Maybe if clipboard permission is not allowed?
        appNotify(`Paste Error: ${JSON.stringify(err)}`, 'error');
      }
    }
  }

  // Clear click, hide validation error message and set import rows to []
  const handleClearClick = () => {
    setImportRows([]);      // clear out rows
    setImportRowsSkippedCount(0);
  }

  const handleExport = () => {
    const timestamp = format(new Date(), '_yyyy-MM-dd_HHmmss');
    const fileName = 'ReVUe_TitlesImport' + timestamp;
    const sheetName = 'Sheet';
    const customizeCell = options => {
      const { excelCell, gridCell } = options;
      const { data, column } = gridCell;
      if (!data?.invalidColumns?.length) return;
      if (data.invalidColumns.some(col => col.name === column.dataField)) { // Check if current cell's column is contained in invalidColumns
        excelCell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: '00ffc9c9' }
        }
      }
    }
    dataGridExtension.exportToExcel(gridRef, fileName, sheetName, customizeCell);
    sendClientLog('Info', 'Import - User exported Titles grid to Excel');
  }

  const onSubmitImport = () => {
    if (canImportTitles) {       // Check if everything can be imported
      // Open confirmation modal
      setConfirmationModalState({
        isModalOpen: true,
        handleConfirmation: handleSubmitImportConfirmation,
        handleCancellation: closeConfirmationModal,
        titleText: 'Confirm',
        contentText: `Are you sure you want to import ${importRowsTitles.length} titles?`
      })
    } else if (importRowsTitles.length === 0) {       // Check if there even is data to be imported
      appNotify('Please Add Data', 'warning');
    } else {
      appNotify('There are validation errors in the data', 'warning');
    }
  }

  const handleSubmitImportConfirmation = () => {
    closeConfirmationModal();
    sendClientLog('Info', `Import - User clicked Import for ${importRowsTitles.length} titles.`);
    submitImport(onSubmitImportTitlesSuccess);
  }

  const onSubmitImportTitlesSuccess = () => {
    redirectToImportStatusTab(history);
  }

  const closeConfirmationModal = () => {
    setConfirmationModalState({ isModalOpen: false });
  }

  const getErrorCells = () => {
    let errorArray = [];
    if (importRowsTitles.length) {
      importRowsTitles.forEach(row => {
        if (row.invalidColumns?.length > 0) {
          row.invalidColumns.forEach(column => {
            const err = errorArray.find(el => el.message === column.message);
            if (err) {
              err.count += 1;
            } else {
              errorArray.push({
                message: column.message,
                count: 1
              })
            }
          });
        }
      });
    }

    // Send all notifications in an array
    return errorArray;
  }

  const onRemoveAllClick = errorType => {
    setConfirmationModalState({ // Open confirmation modal
      isModalOpen: true,
      handleConfirmation: () => handleRemoveAllErrorRows(errorType),
      handleCancellation: closeConfirmationModal,
      titleText: 'Confirm',
      contentText: `Are you sure you want to remove all rows with errors?`
    });
  }

  const handleRemoveAllErrorRows = errorType => {
    closeConfirmationModal();
    let filteredRows;
    switch (errorType) {
      case 'format':
        filteredRows = importRowsTitles.filter(row => !row.invalidColumns?.length);
        revalidateImportRows(filteredRows);
        break;
      default: return;
    }
  }

  const errorCells = getErrorCells();
  return (
    <>
      <div className={classes.container}>
        <div className={classes.leftControls}>
          <Button
            variant='contained'
            color='primary'
            className={classes.alignment}
            onClick={onSubmitImport}
            disabled={!importRowsTitles.length || !canImportTitles || titlesGridEditDirty || maxCountExceeded}
          >
            Import
          </Button>
        </div>
        <div className={classes.centerControls}>
          <Typography variant='h5'>Title Import</Typography>
        </div>
        <div className={classes.rightControls}>
          <Button variant='outlined' color='primary' className={classes.alignment} onClick={handlePaste}>
            Paste from Clipboard
          </Button>
          <Button
            variant='outlined'
            color='primary'
            className={classes.alignment}
            style={{ marginLeft: '4px', marginRight: '4px' }}
            onClick={handleClearClick}
            disabled={!importRowsTitles.length}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExport}
            disabled={!importRowsTitles.length || titlesGridEditDirty}
          >
            Export
          </Button>
          <Divider className={classes.verticalDivider} />
          <a href={xlsxSampleSheet} download="ReVUe_SampleImportTitle.xlsx" className={classes.xlsxSampleButton}>
            <Tooltip title="Download title import template">
              <Button variant='outlined' color='primary' className={classes.alignment}>
                Sample Sheet
            </Button>
            </Tooltip>
          </a>
        </div>
      </div>
      <FileDropZone
        acceptedMimeTypes={[...excel.excelMimeTypes, 'text/plain']}
        onFilesAdded={onDropZoneFilesAdded}
        onFilesRejected={onDropZoneFilesRejected}
        elevation={2}
        dragOverElevation={10}
        className='gridFileDropZone'
        style={{ cursor: 'pointer', minHeight: '70px' }}
      />
      {(errorCells.length || importRowsSkippedTitles) && !maxCountExceeded
        ? <ImportExpander
          title='Cell errors found'
        >
          <div>
            <FormControlLabel
              control={<Switch checked={filterByErrors} onChange={() => setFilterByErrors(!filterByErrors)} />}
              label="Filter by rows with errors"
              disabled={!hasErrors}
            />
            <Tooltip title='Remove all rows with errors'>
              <span>
                <Button
                    variant="outlined"
                    className={classes.redButton}
                    size='small'
                    style={{ marginLeft: '4px', marginRight: '4px' }}
                    onClick={() => onRemoveAllClick('format')}
                    disabled={!errorCells.length || titlesGridEditDirty || maxCountExceeded}
                >
                  Remove All
                </Button>
              </span>
            </Tooltip>
          </div>
          {errorCells.map(error => {
            return (
              <div key={error.message} className={classes.errorTypeContainer}>
                <InlineBadge className={classes.errorCountBadge} badgeContent={error.count} error color='primary' />
                <Typography>{error.message}</Typography>
              </div>
            )
          })}
          {importRowsSkippedTitles > 0
            ? <div className={classes.errorTypeContainer}>
              <InlineBadge className={classes.errorCountBadge} badgeContent={importRowsSkippedTitles} color='primary' />
              <Typography>Row skipped - no ID found</Typography>
            </div>
            : null
          }
        </ImportExpander>
        : null
      }
      <Paper style={{ flexGrow: 1, overflow: 'hidden' }} className={classes.gridPaper}>
        <ImportTitlesGrid
          ref={gridRef}
        />
      </Paper>
    </>
  )
};

const styles = theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1)
  },
  leftControls: {
    display: 'flex',
    width: '40%',
    alignItems: 'center',
    minHeight: 68
  },
  rightControls: {
    display: 'flex',
    width: '40%',
    justifyContent: 'flex-end',
    marginBottom: 5
  },
  centerControls: {
    display: 'flex',
  },
  alignment: {
    margin: 0,
    verticalAlign: 'middle'
  },
  xlsxSampleButton: {
    textDecoration: 'none'        // Remove text decoration underline from anchor tag to make it look like a button
  },
  verticalDivider: {
    width: 1,
    height: 36,
    margin: '0px 8px',
  },
  gridPaper: {
    padding: '5px 8px 8px'
  },
  errorTypeContainer: {
    display: 'flex',
    marginBottom: 1
  },
  errorCountBadge: {
    marginRight: 8
  },
  redButton: {
    color: theme.palette.red.main,
    border: `1px solid ${theme.palette.red.main}80`,
    '&:hover': {
      border: `1px solid ${theme.palette.red.main}`,
      backgroundColor: `${theme.palette.red.main}0a`
    }
  }
});

const mapStateToProps = (state) => {
  const titlesState = importTypeStateSelector(state, Constants.ImportType.TITLES);
  const canImport = canImportSelector(state, Constants.ImportType.TITLES);
  return {
    canImportTitles: canImport,
    importRowsTitles: titlesState.importRows,
    titlesGridEditDirty: titlesState.gridEditDirty,
    importRowsSkippedTitles: titlesState.importRowsSkippedCount
  }
};
const mapDispatchToProps = (dispatch) => {
  const importType = Constants.ImportType.TITLES;
  return {
    setConfirmationModalState: confirmationModalState => dispatch(setConfirmationModalState(confirmationModalState)),
    setImportRows: importRows => dispatch(setImportRows({ importType, importRows })),
    setImportRowsSkippedCount: importRowsSkippedCount => dispatch(setImportRowsSkippedCount({ importType, importRowsSkippedCount })),
    revalidateImportRows: (rows) => dispatch(revalidateImportRows({ importType, rows })),
    submitImport: onSubmitImportSuccess => dispatch(submitImport({ importType, onSubmitImportSuccess })),
    redirectToImportStatusTab: history => {
      history.push(Constants.Routes.UNDERWRITE_IMPORT_STATUS);
    },
    setImportRawData: (data, source) => dispatch(setImportRawData({ data, importType, source })),
    setImportLoadingOverlayState: (importLoadingOverlayState) => dispatch(setImportLoadingOverlayState({ importType, importLoadingOverlayState })),
    appNotify: (message, variant, options) => dispatch(appNotify(message, variant, options))
  }
};
export default compose(
  withRouter,
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps)
)(ImportTitlesControls)